import type { ReactNode } from 'react'
import {
  INVESTOR_NAV_CLASSIC,
  INVESTOR_NAV_ITEMS,
  INVESTOR_NAV_SETTINGS,
  type InvestorNavItem,
} from '../lib/investor-nav'
import { AppLayout } from './app-layout'
import type { NavBadgeValue } from './app-sidebar'
import { CreditBadge } from './credit-badge'
import { StartupSwitcher, type StartupSwitcherItem } from './startup-switcher'
import { UserMenu, type UserMenuLink } from './user-menu'
import { WalletButton } from './wallet-button'

export interface InvestorLayoutProps {
  children: ReactNode
  activeNav: string
  /** Credit balance; shown on the wallet button while above zero */
  creditBalance?: number
  /** Base path prefix for all navigation links (e.g. "/app/investor/abc123") */
  basePath?: string
  userName?: string
  userEmail?: string
  onSignOut?: () => void
  onSendFeedback?: () => void
  activeInvestor?: StartupSwitcherItem
  investors?: StartupSwitcherItem[]
  onSwitchInvestor?: (investorId: string) => void
  logoSrc?: string
  /** Called when clicking the investor name — use for navigation to the app's root */
  onInvestorNameClick?: () => void
  /** Extra links in user dropdown (e.g. admin link) */
  userMenuExtraLinks?: UserMenuLink[]
  /** `classic`: Dashboard, Deal Flow, Feed and Settings, for investors the AI assistant is not open for; `pending`: no menu until it is known which */
  variant?: 'assistant' | 'classic' | 'pending'
  /** A panel beside the page (see AppLayout) */
  sidePanel?: ReactNode
  /** Nav badges keyed by item label, for news the investor has not seen (new watchdog matches on Search). */
  navBadges?: Record<string, NavBadgeValue | undefined>
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

export function InvestorLayout({
  children,
  activeNav,
  basePath = '',
  creditBalance,
  userName,
  userEmail,
  onSignOut,
  onSendFeedback,
  activeInvestor,
  investors = [],
  onSwitchInvestor,
  logoSrc,
  onInvestorNameClick,
  userMenuExtraLinks,
  variant = 'assistant',
  sidePanel,
  navBadges,
}: InvestorLayoutProps) {
  const displayName = userName ?? 'User'
  const displayEmail = userEmail ?? ''

  const withBasePath = (item: InvestorNavItem) => ({
    ...item,
    to: `${basePath}${item.to}`,
    badge: navBadges?.[item.label],
  })
  const classic = variant === 'classic'
  const pending = variant === 'pending'
  const navItems = (classic ? INVESTOR_NAV_CLASSIC : pending ? [] : INVESTOR_NAV_ITEMS).map(
    withBasePath
  )

  return (
    <AppLayout
      activeNav={activeNav}
      navItems={navItems}
      collapsible={classic ? true : undefined}
      sidebarFooterItem={classic || pending ? undefined : withBasePath(INVESTOR_NAV_SETTINGS)}
      defaultCollapsed={false}
      logoSrc={logoSrc}
      // The app's root forwards to the first page, and the brand is where
      // everyone reaches for it.
      homeTo={basePath}
      sidePanel={sidePanel}
      topbarLeft={
        activeInvestor ? (
          <StartupSwitcher
            active={activeInvestor}
            startups={investors}
            onSwitch={onSwitchInvestor ?? (() => {})}
            onNameClick={onInvestorNameClick}
          />
        ) : undefined
      }
      topbarNotifications={
        pending ? null : classic ? (
          <CreditBadge balance={creditBalance} to={`${basePath}/credits`} />
        ) : (
          <WalletButton balance={creditBalance} to={`${basePath}/wallet`} />
        )
      }
      topbarRight={
        <UserMenu
          initials={getInitials(userName, userEmail)}
          name={displayName}
          email={displayEmail}
          profileTo={`${basePath}/me`}
          settingsTo={`${basePath}/preferences`}
          extraLinks={userMenuExtraLinks}
          onSignOut={onSignOut}
          onSendFeedback={onSendFeedback}
        />
      }
    >
      {children}
    </AppLayout>
  )
}
